import type { MutableRefObject } from "react";
import { useEffect, useLayoutEffect, useRef } from "react";

import { useExplorerGlobals } from "contexts/explorerGlobals";
import { useSvgRenderer } from "contexts/svgRenderer";
import { ELEMENT_IDS } from "features/bookmarks/utils";
import { log } from "utils/log";

import { LibSvgRenderer } from "./LibSvgRenderer";

// Extend Window interface for svgRenderer
declare global {
    interface Window {
        svgRenderer?: LibSvgRenderer;
    }
}

const l = log.svgRenderer.wrapper;

export default function SvgRenderer({
    renderFnRef,
}: {
    renderFnRef: MutableRefObject<((moved: boolean, idleFrame?: boolean) => void) | undefined>;
}) {
    const {
        state: { size, view },
    } = useExplorerGlobals();
    const svgRendererRef = useSvgRenderer();
    const svgElRef = useRef<SVGSVGElement | null>(null);

    if (!view) {
        throw new Error("View not initialized");
    }

    useLayoutEffect(() => {
        const svgEl = svgElRef.current;
        if (!svgEl) {
            l.debug("useLayoutEffect - SVG element ref not available, skipping initialization");
            return;
        }

        l.debug("useLayoutEffect - Initializing svgRendererRef.current to new LibSvgRenderer()");

        svgRendererRef.current = new LibSvgRenderer(svgEl);

        // Expose to window primarily for browser console testing and debugging; this is not a formal public API.
        if (globalThis.window !== undefined) {
            globalThis.window.svgRenderer = svgRendererRef.current;
            l.debug("SVG Renderer exposed to window.svgRenderer");
        }

        const instanceToCleanup = svgRendererRef.current;

        return () => {
            svgRendererRef.current = null;

            if (globalThis.window?.svgRenderer === instanceToCleanup) {
                delete globalThis.window.svgRenderer;
                l.debug("SVG Renderer removed from window.svgRenderer");
            }
        };
    }, [svgRendererRef, view.convert.worldSpaceToScreenSpaceConverter]);

    useEffect(() => {
        l.debug("useEffect - setting renderFnRef.current to render");
        renderFnRef.current = async (moved: boolean) => {
            if (!svgRendererRef.current) {
                throw new Error("SVG Renderer not initialized");
            }

            const {
                renderState: { camera },
            } = view;
            await svgRendererRef.current.render({
                moved,
                camera,
                size,
            });
        };
        return () => (renderFnRef.current = undefined);
    }, [size, view, renderFnRef, svgRendererRef]);

    return (
        <svg
            ref={svgElRef}
            id={ELEMENT_IDS.markupContainer}
            width={size.width}
            height={size.height}
            style={{
                position: "absolute",
                left: 0,
                top: 0,
                pointerEvents: "none",
            }}
        ></svg>
    );
}
